import React, { useEffect, useState } from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { getDominanceHistory, DominanceHistoryPoint } from '../../services/macroMarketHistoryService';

interface MiniDominanceGraphProps {}

function formatSignedDelta(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)} pts`;
}

export const MiniDominanceGraph: React.FC<MiniDominanceGraphProps> = () => {
  const [history, setHistory] = useState<DominanceHistoryPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getDominanceHistory()
      .then(res => {
        setHistory(res.data);
        setLoading(false);
      })
      .catch(e => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          p: 2.4,
          borderRadius: 5,
          background: 'linear-gradient(180deg, #ffffff 0%, #fbf7ef 100%)',
          border: '1px solid rgba(16,37,43,0.08)',
        }}
      >
        <Typography variant="body2" sx={{ color: '#556970' }}>
          Chargement de l’historique de dominance...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!history.length) {
    return (
      <Alert severity="info" sx={{ borderRadius: 3 }}>
        Aucune donnée historique de dominance disponible.
      </Alert>
    );
  }

  const safeHistory = history
    .map((pt) => ({
      timestamp: Number(pt.timestamp),
      btcDominance: Number(pt.btcDominance),
      ethDominance: Number(pt.ethDominance),
    }))
    .filter(
      (pt) => Number.isFinite(pt.timestamp)
        && Number.isFinite(pt.btcDominance)
        && Number.isFinite(pt.ethDominance),
    )
    .sort((left, right) => left.timestamp - right.timestamp);

  if (!safeHistory.length) {
    return (
      <Alert severity="info" sx={{ borderRadius: 3 }}>
        Les données de dominance reçues sont inexploitables.
      </Alert>
    );
  }

  const btcStart = safeHistory[0]?.btcDominance ?? 0;
  const btcEnd = safeHistory[safeHistory.length - 1]?.btcDominance ?? 0;
  const ethStart = safeHistory[0]?.ethDominance ?? 0;
  const ethEnd = safeHistory[safeHistory.length - 1]?.ethDominance ?? 0;
  const btcVar = btcEnd - btcStart;
  const ethVar = ethEnd - ethStart;

  const width = 220;
  const height = 72;
  const chartPadding = 6;
  const dominanceValues = safeHistory.flatMap((pt) => [pt.btcDominance, pt.ethDominance]);
  const rawMin = Math.min(...dominanceValues);
  const rawMax = Math.max(...dominanceValues);
  const paddedRange = Math.max(rawMax - rawMin, 1);
  const lowerBound = Math.max(0, rawMin - Math.max(paddedRange * 0.2, 1));
  const upperBound = rawMax + Math.max(paddedRange * 0.2, 1);
  const valueRange = Math.max(upperBound - lowerBound, 1);
  const divisor = safeHistory.length > 1 ? (safeHistory.length - 1) : 1;
  const scaleX = (index: number) => (safeHistory.length === 1 ? width / 2 : (index / divisor) * width);
  const scaleY = (value: number) => {
    const ratio = (value - lowerBound) / valueRange;
    return height - chartPadding - (ratio * (height - chartPadding * 2));
  };
  const btcPoints = safeHistory.map((pt, i) => {
    const x = scaleX(i);
    const y = scaleY(pt.btcDominance);
    return `${x},${y}`;
  }).join(' ');
  const ethPoints = safeHistory.map((pt, i) => {
    const x = scaleX(i);
    const y = scaleY(pt.ethDominance);
    return `${x},${y}`;
  }).join(' ');
  const currentPointCountLabel = safeHistory.length > 1 ? `${safeHistory.length} points` : '1 point disponible';

  return (
    <Box
      sx={{
        p: 2.4,
        borderRadius: 5,
        background: 'linear-gradient(180deg, #ffffff 0%, #fbf7ef 100%)',
        border: '1px solid rgba(16,37,43,0.08)',
        boxShadow: '0 14px 34px rgba(16,37,43,0.06)',
      }}
    >
      <Typography variant="overline" sx={{ color: '#6d7e84', letterSpacing: '0.12em' }}>
        Historique 7 jours
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 800, color: '#17323c', mb: 1.2 }}>
        Dominance BTC / ETH
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: '#6d7e84', mb: 1.1 }}>
        {currentPointCountLabel}
      </Typography>
      <Box sx={{ p: 1.5, borderRadius: 4, background: '#f7f1e5', border: '1px solid rgba(16,37,43,0.05)' }}>
        <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="96" style={{ display: 'block' }}>
          {safeHistory.length > 1 && (
            <polyline points={btcPoints} fill="none" stroke="#173f5f" strokeWidth={3} strokeLinecap="round" />
          )}
          {safeHistory.length > 1 && (
            <polyline points={ethPoints} fill="none" stroke="#8d6c16" strokeWidth={3} strokeLinecap="round" />
          )}
          {safeHistory.map((pt, i) => (
            <circle
              key={`btc-${pt.timestamp}`}
              cx={scaleX(i)}
              cy={scaleY(pt.btcDominance)}
              r={safeHistory.length > 1 ? 2.5 : 4}
              fill="#173f5f"
            />
          ))}
          {safeHistory.map((pt, i) => (
            <circle
              key={`eth-${pt.timestamp}`}
              cx={scaleX(i)}
              cy={scaleY(pt.ethDominance)}
              r={safeHistory.length > 1 ? 2.5 : 4}
              fill="#8d6c16"
            />
          ))}
        </svg>
      </Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mt: 1.5 }}>
        <Box sx={{ px: 1.2, py: 0.9, borderRadius: 3, background: 'rgba(23,63,95,0.08)' }}>
          <Typography variant="caption" sx={{ display: 'block', color: '#173f5f', fontWeight: 800 }}>
            BTC.D actuel
          </Typography>
          <Typography variant="body2" sx={{ color: '#17323c', fontWeight: 700 }}>
            {btcEnd.toFixed(2)}%
          </Typography>
          <Typography variant="caption" sx={{ color: '#556970' }}>
            Variation 7j {formatSignedDelta(btcVar)}
          </Typography>
        </Box>
        <Box sx={{ px: 1.2, py: 0.9, borderRadius: 3, background: 'rgba(141,108,22,0.1)' }}>
          <Typography variant="caption" sx={{ display: 'block', color: '#8d6c16', fontWeight: 800 }}>
            ETH.D actuel
          </Typography>
          <Typography variant="body2" sx={{ color: '#17323c', fontWeight: 700 }}>
            {ethEnd.toFixed(2)}%
          </Typography>
          <Typography variant="caption" sx={{ color: '#556970' }}>
            Variation 7j {formatSignedDelta(ethVar)}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default MiniDominanceGraph;
